import SvgChevronright from '@common/__generated__/icon/SvgChevronright';
import { Skeleton } from '@common/components';
import { Lottie } from '@common/components/atom/Lottie';
import { cn } from '@core/utils';

import { CustomLink } from '@/app/_components/CustomLink';
import { Rating } from '@/app/donation/_components/Rating';
import { Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';

interface GiftListItemProps {
  /**
   * 이미지 최적화를 위해서 반드시 설정
   *
   * @example
   * ```
   * mainImageSizes={`${BREAKPOINTS.MOBILE} 50vw, ${BREAKPOINTS.TABLET} 33vw, 285px`}
   * ```
   */
  mainImageSizes?: string;
  mainImageUrl?: string;
  productName?: string;
  donationAmount?: number;
  reviewRating?: number;
  reviewCount?: number;
  institution?: {
    logoUrl: string;
    id: number | string;
    name: string;
  };
  productNumber?: string;
  className?: string;
  isLoading?: boolean;
  isPick?: boolean;
  isNew?: boolean;
  isAdult?: boolean;
  isSoldOut?: boolean;
  onGiftLinkClick?: () => void;
  responsiveClassName?: {
    institutionLogo?: string;
    institutionName?: string;
    productName?: string;
    donationAmount?: string;
    donationAmountText?: string;
  };
}

export const GiftListItem = ({
  mainImageSizes,
  mainImageUrl,
  productName,
  donationAmount,
  reviewRating,
  reviewCount,
  institution,
  productNumber,
  className,
  isLoading,
  isPick,
  isNew,
  isAdult,
  isSoldOut,
  onGiftLinkClick,
  responsiveClassName,
}: GiftListItemProps) => {
  if (isLoading) return <Loading />;

  return (
    <article className={cn('flex flex-col gap-8', className)}>
      <CustomLink
        href={
          productNumber
            ? DONATION_ROUTES.GIFTS_DETAIL.pathname({
                productNumber,
              })
            : '#'
        }
        aria-label={`${productName} 상세 페이지로 이동`}
        onClick={onGiftLinkClick}
        className={cn('relative aspect-square w-full rounded-xlarge', {
          'pointer-events-none': !productNumber,
        })}
      >
        <div className="relative size-full overflow-hidden rounded-xlarge">
          <Image
            src={mainImageUrl || ''}
            alt={productName ?? '답례품 이미지'}
            className="object-cover"
            sizes={mainImageSizes}
            fill
          />
        </div>
        <div className="absolute left-0 top-0 flex">
          {isPick && (
            <div className="size-fit overflow-hidden rounded-xxsmall">
              <Lottie src="/assets/jsons/pick-badge.json" />
            </div>
          )}
          {isNew && <NewBadge />}
        </div>
        {isAdult && (
          <div className="absolute bottom-16 right-16 rounded-xsmall bg-B-500 px-6 py-2">
            <p className="font-600 text-Gray-white typo-caption1">
              19세성인인증
            </p>
          </div>
        )}
        {isSoldOut && (
          <div className="absolute inset-0 rounded-xlarge bg-Gray-black/40 flex-center">
            <span className="font-700 text-Gray-white typo-body2">
              품절되었어요
            </span>
          </div>
        )}
      </CustomLink>

      <div className="flex flex-col">
        {institution ? (
          <CustomLink
            className="flex items-center gap-4"
            href={DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
              id: institution.id,
            })}
            aria-label={`${institution.name} 상세 페이지로 이동`}
          >
            <div
              className={cn(
                'relative size-24 overflow-hidden rounded-pill border-[1.2px] border-Gray-300',
                responsiveClassName?.institutionLogo,
              )}
            >
              <Image
                fill
                src={institution.logoUrl}
                alt={`${institution.name} 로고 이미지`}
                className="object-cover"
                sizes="24px"
              />
            </div>
            <p
              className={cn(
                'font-600 text-Gray-600 typo-body4',
                responsiveClassName?.institutionName,
              )}
            >
              {institution.name}
            </p>
            <SvgChevronright className="size-16 text-Gray-600" />
          </CustomLink>
        ) : null}

        {productName ? (
          <CustomLink
            href={
              productNumber
                ? DONATION_ROUTES.GIFTS_DETAIL.pathname({
                    productNumber,
                  })
                : '#'
            }
            aria-label={`${productName} 상세 페이지로 이동`}
            onClick={onGiftLinkClick}
            className={cn(
              'line-clamp-2 pt-4 font-500 text-Gray-800 transition-colors typo-body3 hover:text-Gray-700',
              responsiveClassName?.productName,
              {
                'pointer-events-none': !productNumber,
              },
            )}
          >
            {productName}
          </CustomLink>
        ) : null}

        {donationAmount ? (
          <div className="flex flex-wrap items-center gap-x-4 pt-8">
            <p className="font-700 text-Gray-black typo-body2">
              {donationAmount.toLocaleString()}
              <span className="font-600 typo-body3">원</span>
            </p>
            <p
              className={cn(
                'font-600 text-Gray-550 typo-body3',
                responsiveClassName?.donationAmountText,
              )}
            >
              기부시 증정
            </p>
          </div>
        ) : null}

        <Rating
          value={reviewRating ?? 0}
          count={reviewCount}
          size={16}
          contentClassName={{
            count: 'typo-caption1 mt-2',
          }}
          className="pt-2"
        />
      </div>
    </article>
  );
};

const Loading = () => {
  return (
    <div className={cn('flex flex-col gap-8')}>
      <Skeleton className="aspect-[1/1] size-full rounded-xlarge" />

      <div className="flex flex-col">
        <Skeleton className="h-24 w-100 rounded-xsmall" />

        <Skeleton className="mt-4 h-42 w-full rounded-xsmall" />

        <Skeleton typo="body2" className="mt-8 w-150 rounded-xsmall" />

        <Skeleton typo="caption1" className="mt-2 w-108 rounded-xsmall" />
      </div>
    </div>
  );
};

const NewBadge = () => {
  return (
    <div className="h-24 w-40 rounded-xxsmall bg-P-550 pt-2 font-700 text-Gray-white flex-center typo-caption1">
      NEW
    </div>
  );
};
